// why by fields? So we don't have to import the whole project into each component which causes unnecessary re-renders

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  saveWorkingProjectByFields,
  setSavingWorkingProjectError,
  setSavingWorkingProject,
  setWorkingProject,
  clearSaveSessionAfterProjectSave,
  loadProject,
  updateMostRecentActivity,
} from '..';
import { SaveProject } from '@/connection/projects/save-project';
import { RootState } from '@/store/main';
import { BrandliveEvent } from '@/types/projects.types';
import {
  clearSaveProjectByFieldsTimeout,
  getSaveProjectTimeout,
  setSaveProjectByFieldsTimeoutGlobal,
} from './save-project-timeouts';
import sleep from '@/utils/sleep';
import { UpdateSession } from '@/connection/sessions/update-session';

const saveProjectByFieldsMiddleware = createListenerMiddleware();

saveProjectByFieldsMiddleware.startListening({
  matcher: isAnyOf(saveWorkingProjectByFields), // comma separated list of actions to listen for
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(setSavingWorkingProject(true));

    const saveProjectTimeout = getSaveProjectTimeout();
    // recursive function that waits for the saveProjectTimeout to be cleared otherwise we could accidentally wipe out the changes
    if (saveProjectTimeout) {
      console.warn('saveWorkingProjectByFields called while saveWorkingProject is still pending');
      // wait for the byFieldstimeout to be cleared
      const checkSaveProjetTimeout = async () => {
        const t = getSaveProjectTimeout();
        // console.log('saveProjectTimeout', t);
        if (!t) return;
        console.warn('waiting for saveWorkingProject to finish');
        await sleep(100);
        return checkSaveProjetTimeout();
      };
      await checkSaveProjetTimeout();
    }

    clearSaveProjectByFieldsTimeout();

    setSaveProjectByFieldsTimeoutGlobal(
      setTimeout(async () => {
        const state = listenerApi.getState() as RootState;
        const project = state?.project?.workingProject;
        const sessions = state.project?.sessionsToUpdateAfterProjectSave;
        const shouldRefetchProject = state.project.refetchProjectAfterSave;

        if (project) {
          try {
            const saveProjectResult = await SaveProject(project);
            if (sessions?.length) {
              const promises = sessions.map((session) => UpdateSession(session));
              await Promise.all(promises);
            }

            // this fixes issue where homepage translations do not update in redux when a new item is added into a modules
            if (saveProjectResult?.homepage?.modules && project?.homepage?.modules) {
              const updatedProjectHomepage: BrandliveEvent = {
                ...project,
                homepage: {
                  ...project.homepage,
                  modules: saveProjectResult.homepage.modules,
                },
              };

              listenerApi.dispatch(setWorkingProject(updatedProjectHomepage));
            }

            listenerApi.dispatch(setSavingWorkingProjectError(null));
            listenerApi.dispatch(updateMostRecentActivity({ isPublish: false }));
          } catch (error: unknown) {
            const errMsg = error instanceof Error ? error.message : 'Error saving working project';
            console.error('Error saving project', error);
            listenerApi.dispatch(setSavingWorkingProjectError(errMsg));
          } finally {
            listenerApi.dispatch(setSavingWorkingProject(false));
            listenerApi.dispatch(clearSaveSessionAfterProjectSave());
            if (shouldRefetchProject && state.project?.workingProject?.uuid) {
              listenerApi.dispatch(
                loadProject({ uuid: state.project.workingProject.uuid, doNotSetLoadingState: true })
              );
            }
            clearSaveProjectByFieldsTimeout();
          }
        }
      }, 1000)
    );
  },
});

export default saveProjectByFieldsMiddleware;
